import React from 'react';
import { FadeIn } from './ui/FadeIn';
import { ActivityIcon, BrainIcon, FingerprintIcon, UsersIcon, CompassIcon, ArrowRightIcon, RotateCcwIcon } from 'lucide-react';
const CHAIN = ['Pensamiento', 'Emoción', 'Estado', 'Decisión', 'Acción', 'Resultado'];
const PILLARS = [{
  name: 'Cuerpo',
  icon: ActivityIcon,
  desc: 'Regulación física: comida, movimiento, sueño e hidratación. Cuando le das al cuerpo señales de seguridad, la mente baja el volumen.'
}, {
  name: 'Mente',
  icon: BrainIcon,
  desc: 'Claridad mental: vaciás el ruido, observás tus pensamientos y dejás de confundir una emoción con un hecho.'
}, {
  name: 'Identidad',
  icon: FingerprintIcon,
  desc: 'Quién sos de verdad, no el personaje que sostenés para afuera. Acá se cae la máscara y aparece la certeza.'
}, {
  name: 'Familia',
  icon: UsersIcon,
  desc: 'El impacto real. Cuando volvés a vos, tu casa lo siente. Presencia en lugar de restos de energía.'
}, {
  name: 'Propósito',
  icon: CompassIcon,
  desc: 'Decisiones tomadas desde dirección, no desde miedo. Lo que hacés empieza a tener sentido con quien sos.'
}];
const PHASES = [{
  weeks: 'Semanas 1–4',
  title: 'VER',
  desc: 'Identificás tu loop específico. No el loop en general: el tuyo, con nombre y apellido.'
}, {
  weeks: 'Semanas 5–8',
  title: 'ELEGIR',
  desc: 'Interrumpís el patrón conscientemente. Empezás a decidir en vez de reaccionar.'
}, {
  weeks: 'Semanas 9–12',
  title: 'CONSTRUIR',
  desc: 'Operás desde certeza, no desde miedo. La coherencia pasa a ser tu punto de partida.'
}];
export function MechanismSection() {
  return <section id="mechanism" className="py-14 md:py-32 bg-charcoal-dark border-y border-charcoal-light" data-id="element-228" data-mp="3g4zdorf9430r">
      <div className="max-w-6xl mx-auto px-6" data-id="element-229" data-mp="19fugv8cn290i">
        <FadeIn className="text-center mb-10 md:mb-16 max-w-3xl mx-auto" data-id="element-230" data-mp="1ee5qbhz0uhi7">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-offwhite mb-6" data-id="element-231" data-mp="1ecbd6ptzrtoo">
            El Mecanismo — Por qué ELEVATE funciona
          </h2>
          <p className="text-lg text-offwhite-dim" data-id="element-232" data-mp="17x46i1iqwpix">
            Tu realidad no es un accidente. Se construye en cadena:
          </p>

          <div className="mt-8 flex flex-wrap justify-center items-center gap-x-3 gap-y-2 text-sm md:text-base font-medium text-sand" data-id="element-233" data-mp="1315cnyenefb4">
            {CHAIN.map((step, i) => <React.Fragment key={step} data-id="element-234" data-mp="1vmf3d7wz5zrs" data-mpi={i}>
                <span className={i === CHAIN.length - 1 ? 'text-amber font-bold' : undefined} data-id="element-235" data-mp="1qon8q64y3zjw">
                  {step}
                </span>
                {i < CHAIN.length - 1 && <ArrowRightIcon className="w-4 h-4 text-charcoal-light" data-id="element-236" data-mp="1hvtp5wigue08" />}
              </React.Fragment>)}
            <span className="flex items-center gap-2 text-offwhite-dim/60 text-xs uppercase tracking-wider" data-id="element-237" data-mp="29nj4hlwgclzu">
              <RotateCcwIcon className="w-4 h-4" data-id="element-238" data-mp="3saiy8x41pfo2" />
              vuelve al inicio
            </span>
          </div>

          <p className="mt-8 text-offwhite-dim" data-id="element-239" data-mp="136f4cjlqs5i">
            Si repetís los mismos pensamientos, sentís lo mismo. Si sentís lo
            mismo, tomás las mismas decisiones. Si tomás las mismas decisiones,
            tu vida no cambia.
          </p>
          <p className="mt-4 text-xl font-heading font-bold text-offwhite" data-id="element-240" data-mp="a2jqxbv77ste">
            ELEVATE interrumpe ese loop desde 5 frentes integrados:
          </p>
        </FadeIn>

        {/* 5 PILLARS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-241" data-mp="k0ppbjhsqgti">
          {PILLARS.map((pillar, i) => {
          const Icon = pillar.icon;
          const isLead = i === 0;
          return <FadeIn key={pillar.name} delay={i * 0.08} direction="up" className={`bg-charcoal border border-charcoal-light p-6 md:p-8 rounded-sm flex flex-col ${isLead ? 'lg:col-span-1' : ''}`} data-id="element-242" data-mp="xuh3542k8mv1" data-mpi={i}>
                <div className="flex items-center gap-4 mb-4" data-id="element-243" data-mp="k81fyw9f665y">
                  <div className="bg-sand/10 p-3 rounded-sm shrink-0" data-id="element-244" data-mp="k5nosyhv1e3o">
                    <Icon className="w-6 h-6 text-sand" data-id="element-245" data-mp="yvv10wh9y0vx" />
                  </div>
                  <h3 className="text-lg font-heading font-bold text-offwhite uppercase tracking-wider" data-id="element-246" data-mp="tycq50jp5bla">
                    {pillar.name}
                  </h3>
                </div>
                <p className="text-offwhite-dim text-sm md:text-base leading-relaxed" data-id="element-247" data-mp="19fhzq978v69">
                  {pillar.desc}
                </p>
              </FadeIn>;
        })}

          <FadeIn delay={0.4} direction="up" className="bg-sand/10 border border-sand/30 p-6 md:p-8 rounded-sm flex flex-col justify-center" data-id="element-248" data-mp="b73fgny6wgs4">
            <p className="text-lg md:text-xl font-heading font-bold text-sand leading-snug" data-id="element-249" data-mp="1mm8tgo3ru1r1">
              Los 5 pilares no se trabajan por separado. Se trabajan
              conectados, en progresión.
            </p>
            <p className="mt-3 text-sm text-offwhite-dim" data-id="element-250" data-mp="1z4uowzvbr3hm">
              Por eso el cambio se sostiene cuando el programa termina.
            </p>
          </FadeIn>
        </div>

        {/* PHASES */}
        <div className="mt-12 md:mt-20" data-id="element-251" data-mp="1p5n11iqyrqg9">
          <FadeIn data-id="element-252" data-mp="1p6onejdfjzda">
            <h3 className="text-xl md:text-2xl font-heading font-bold text-offwhite text-center mb-8 md:mb-12" data-id="element-253" data-mp="1p883a8v6c8ej">
              Las 12 semanas, en 3 fases
            </h3>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-4 md:gap-6" data-id="element-254" data-mp="2hx6m7874fekw">
            {PHASES.map((phase, i) => <FadeIn key={phase.title} delay={i * 0.12} direction="up" className="relative bg-charcoal border-t-2 border-sand p-6 md:p-8 rounded-sm flex flex-col" data-id="element-255" data-mp="241utt7gfu075" data-mpi={i}>
                <span className="text-xs font-medium text-offwhite-dim/60 uppercase tracking-[0.2em]" data-id="element-256" data-mp="30mlp786dadrx">
                  {phase.weeks}
                </span>
                <h4 className="mt-3 text-2xl font-heading font-bold text-sand tracking-wider" data-id="element-257" data-mp="3af7ps81f4ukv">
                  {phase.title}
                </h4>
                <p className="mt-4 text-offwhite-dim leading-relaxed" data-id="element-258" data-mp="31ft03at2so1f">
                  {phase.desc}
                </p>
              </FadeIn>)}
          </div>
        </div>

        <FadeIn delay={0.2} className="mt-10 md:mt-16 text-center" data-id="element-259" data-mp="2riy3ixgd4xps">
          <p className="text-xl md:text-2xl font-heading font-bold text-sand" data-id="element-260" data-mp="2mrzoe17425o4">
            12 semanas · 1 sesión por semana · Ejercicios de 10–20 minutos
            diarios
          </p>
          <p className="mt-3 text-lg text-offwhite-dim" data-id="element-261" data-mp="2io08nktnnavt">
            No perfecto.{' '}
            <span className="text-offwhite font-bold" data-id="element-262" data-mp="2dp6wbvq3tpx8">Completo.</span>
          </p>
        </FadeIn>
      </div>
    </section>;
}